import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Shell from "../components/Shell";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ZoomableImage from "../components/ZoomableImage";
import { ArrowLeft, ShieldAlert, ShieldCheck, Lock, Sparkles, ChevronDown } from "lucide-react";

function SeverityDots({ severity }) {
  const levels = { none: 0, mild: 1, moderate: 2, severe: 3 };
  const n = levels[severity] || 0;
  const color = n === 3 ? "#D32F2F" : n === 2 ? "#FF8F00" : "#4CAF50";
  return (
    <div className="flex gap-1.5">
      {[1, 2, 3].map((i) => (
        <span
          key={i}
          className="w-2.5 h-2.5 rounded-full"
          style={{
            background: i <= n ? color : "rgba(255,255,255,0.15)",
            boxShadow: i <= n ? `0 0 10px ${color}` : "none",
          }}
        />
      ))}
    </div>
  );
}

function RemedyCard({ r, index }) {
  const [open, setOpen] = useState(index === 0);
  const typeColor = {
    organic: "bg-[rgba(76,175,80,0.15)] border-[rgba(76,175,80,0.4)] text-[#4CAF50]",
    chemical: "bg-[rgba(255,143,0,0.15)] border-[rgba(255,143,0,0.4)] text-[#FF8F00]",
    environmental: "bg-[rgba(123,31,162,0.18)] border-[rgba(123,31,162,0.4)] text-[#d49bff]",
    preventive: "bg-[rgba(76,175,80,0.12)] border-[rgba(76,175,80,0.35)] text-[#9bd6a0]",
  }[r.treatment_type] || "bg-white/10 border-white/20 text-white/70";

  return (
    <div className={`glass-card overflow-hidden fade-up stagger-${(index % 5) + 1}`} data-testid={`remedy-card-${index}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 p-4 text-left"
      >
        <span
          className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0"
          style={{ background: "rgba(76,175,80,0.18)", color: "#4CAF50", boxShadow: "var(--glow-green)" }}
        >
          {index + 1}
        </span>
        <div className="flex-1">
          <p className="font-semibold text-sm">{r.title}</p>
          <div className="flex gap-2 mt-1 flex-wrap">
            <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${typeColor}`}>
              {r.treatment_type}
            </span>
            <span className="text-[10px] px-2 py-0.5 rounded-full border border-white/15 text-white/60">
              {r.difficulty}
            </span>
          </div>
        </div>
        <ChevronDown size={16} className={`transition-transform text-[#4CAF50] ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-white/5">
          <ol className="mt-3 space-y-2 text-sm text-soft list-decimal pl-5">
            {(r.steps || []).map((s, j) => <li key={j}>{s}</li>)}
          </ol>
          {r.products?.length > 0 && (
            <div className="mt-4">
              <p className="text-[11px] uppercase tracking-wider text-muted-soft mb-2">Suggested products</p>
              <div className="flex gap-2 flex-wrap">
                {r.products.map((p, j) => <span key={j} className="glass-pill">{p.name}</span>)}
              </div>
            </div>
          )}
          {r.timeline && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] uppercase tracking-wider text-muted-soft">Recovery progress</span>
                <span className="text-xs text-soft">{r.timeline}</span>
              </div>
              <div className="progress-track amber" data-testid={`recovery-progress-${index}`}>
                <div className="progress-fill" style={{ width: `${Math.min(100, (index + 1) * 18)}%` }} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function DiagnoseResultScreen() {
  const { scanId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [scan, setScan] = useState(location.state?.result || null);
  const [loading, setLoading] = useState(!scan);

  useEffect(() => {
    if (scan) return;
    (async () => {
      try {
        const { data } = await api.get(`/scans/${scanId}`);
        setScan({ ...data, result: data.result });
      } catch {} finally { setLoading(false); }
    })();
  }, [scan, scanId]);

  if (loading) return <Shell section="diagnose_result"><div className="glass-card p-8 mt-10 text-center"><span className="inline-block w-3 h-3 rounded-full bg-[#FF8F00] dot-pulse" /></div></Shell>;

  const result = scan?.result || {};
  const isHealthy = !!result.is_healthy;

  return (
    <Shell section="diagnose_result">
      <button onClick={() => navigate("/home")} className="flex items-center gap-1 text-[#4CAF50] text-sm mb-3 hover:underline">
        <ArrowLeft size={14} /> Home
      </button>

      {/* Health banner */}
      <div
        className="glass-card-strong p-5 flex items-center gap-4 mb-5 fade-up"
        style={{
          borderColor: isHealthy ? "rgba(76,175,80,0.45)" : "rgba(255,143,0,0.5)",
          boxShadow: isHealthy ? "var(--glow-green)" : "var(--glow-amber)",
        }}
        data-testid="health-banner"
      >
        {isHealthy ? (
          <ShieldCheck size={36} className="text-[#4CAF50]" />
        ) : (
          <ShieldAlert size={36} className="text-[#FF8F00]" />
        )}
        <div className="flex-1">
          <p className="text-xs uppercase tracking-wider text-muted-soft">Status</p>
          <h2 className="text-lg font-bold">{isHealthy ? "Looks healthy!" : "Issue detected"}</h2>
        </div>
      </div>

      {!isHealthy && result.disease && (
        <div className="glass-card p-5 mb-5 fade-up stagger-1" data-testid="disease-card">
          {scan?.image_data && (
            <div className="rounded-lg overflow-hidden mb-4">
              <ZoomableImage
                src={scan.image_data}
                alt="Diagnosed plant"
                thumbHeight={200}
                testid="diagnose-image"
              />
            </div>
          )}
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h3 className="text-lg font-bold">{result.disease.name}</h3>
              <p className="text-[11px] uppercase tracking-wider text-muted-soft mt-1">
                {Math.round(result.disease.probability || 0)}% confidence
              </p>
            </div>
            <SeverityDots severity={result.severity} />
          </div>
          <p className="text-sm text-soft leading-relaxed">{result.disease.description}</p>
          {result.disease.symptoms?.length > 0 && (
            <div className="mt-4">
              <p className="text-[11px] uppercase tracking-wider text-muted-soft mb-2">Symptoms</p>
              <ul className="space-y-1 text-sm text-soft">
                {result.disease.symptoms.map((s, i) => (
                  <li key={i} className="flex gap-2"><span className="text-[#4CAF50]">•</span> {s}</li>
                ))}
              </ul>
            </div>
          )}
          {/* Platinum: affected area + extended detail */}
          {user?.tier === "platinum" && result.disease.affected_areas && (
            <div
              className="mt-4 rounded-xl p-3"
              style={{ background: "rgba(255,213,79,0.10)", border: "1px solid rgba(255,213,79,0.35)" }}
              data-testid="platinum-affected-area"
            >
              <p className="text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: "#FFD54F" }}>
                ✨ Platinum · Affected area
              </p>
              <p className="text-sm text-white">{result.disease.affected_areas}</p>
            </div>
          )}
        </div>
      )}

      {/* PLATINUM Recovery Plan summary --------------------------------------- */}
      {user?.tier === "platinum" && !isHealthy && Array.isArray(result.remedies) && result.remedies.length > 0 && (
        <div
          className="glass-card-strong p-5 mb-5 fade-up stagger-2"
          style={{
            background: "rgba(8,12,18,0.62)",
            border: "1px solid rgba(255,213,79,0.45)",
            boxShadow: "0 12px 32px rgba(0,0,0,0.55), 0 0 22px rgba(255,213,79,0.18)",
            backdropFilter: "blur(18px)",
            WebkitBackdropFilter: "blur(18px)",
          }}
          data-testid="platinum-recovery-plan"
        >
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={18} className="text-[#FFD54F]" />
            <p
              className="text-[11px] uppercase tracking-wider font-extrabold"
              style={{ color: "#FFE082", textShadow: "0 1px 6px rgba(0,0,0,0.7)" }}
            >
              Platinum deep-dive
            </p>
          </div>
          <h3 className="text-lg font-extrabold mb-4 text-white" style={{ textShadow: "0 2px 10px rgba(0,0,0,0.7)" }}>
            Your complete recovery plan
          </h3>

          {/* Treatment mix overview */}
          {(() => {
            const counts = result.remedies.reduce((acc, r) => {
              const k = r.treatment_type || "other";
              acc[k] = (acc[k] || 0) + 1;
              return acc;
            }, {});
            return (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                {Object.entries(counts).map(([k, v]) => (
                  <div
                    key={k}
                    className="rounded-lg p-2.5"
                    style={{ background: "rgba(8,14,10,0.6)", border: "1px solid rgba(255,213,79,0.35)" }}
                  >
                    <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "rgba(255,255,255,0.85)" }}>{k}</p>
                    <p className="text-xl font-extrabold text-white">{v}</p>
                    <p className="text-[10px]" style={{ color: "rgba(255,255,255,0.7)" }}>option{v === 1 ? "" : "s"}</p>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Combined step list */}
          <p className="text-[11px] uppercase tracking-wider font-bold mb-2" style={{ color: "#FFD54F" }}>Recommended sequence</p>
          <ol className="space-y-2 text-sm">
            {result.remedies.map((r, i) => (
              <li
                key={i}
                className="rounded-lg p-2.5 flex items-start gap-3"
                style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.10)" }}
              >
                <span
                  className="w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0"
                  style={{ background: "rgba(255,213,79,0.28)", color: "#FFE082", border: "1px solid rgba(255,213,79,0.6)" }}
                >
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-white">{r.title}</p>
                  <p className="text-[11px]" style={{ color: "rgba(255,255,255,0.78)" }}>
                    {r.treatment_type}
                    {r.difficulty ? ` · ${r.difficulty}` : ""}
                    {r.timeline ? ` · ${r.timeline}` : ""}
                  </p>
                </div>
              </li>
            ))}
          </ol>
          <p className="text-[12px] mt-3 leading-relaxed" style={{ color: "rgba(255,255,255,0.9)" }}>
            <b style={{ color: "#FFD54F" }}>Tip:</b> start with the gentlest (organic / environmental) remedy first. Escalate only if symptoms persist after the timeline ends.
          </p>
        </div>
      )}

      {/* Remedies */}
      {!isHealthy && (
        <div>
          <h3 className="text-base font-semibold uppercase tracking-wider text-soft mb-3">Remedies</h3>
          {result.remedies_locked || user?.tier === "free" ? (
            <div className="glass-card p-6 relative overflow-hidden fade-up" data-testid="remedies-locked">
              <div className="space-y-3 blur-sm">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="glass-card p-4">
                    <div className="h-3 w-1/2 bg-white/20 rounded mb-2" />
                    <div className="h-2 w-3/4 bg-white/10 rounded" />
                  </div>
                ))}
              </div>
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center">
                <Lock size={28} className="text-[#FF8F00]" />
                <p className="font-semibold">{result.remedy_count || 5} remedies available</p>
                <p className="text-sm text-soft">Upgrade to Premium to unlock all treatment options.</p>
                <button onClick={() => navigate("/subscription")} className="btn-amber inline-flex items-center gap-2">
                  <Sparkles size={14} /> Upgrade to Premium
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {(result.remedies || []).map((r, i) => <RemedyCard key={i} r={r} index={i} />)}
            </div>
          )}
        </div>
      )}

      <div className="mt-6 flex gap-3">
        <button onClick={() => navigate("/diagnose")} className="btn-outline flex-1">Scan again</button>
        <button onClick={() => navigate("/collection")} className="btn-primary flex-1">View collection</button>
      </div>
    </Shell>
  );
}
